import '../operations.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Send, Activity, Flame } from 'lucide-react';
import { useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { smtpApi } from '../../api/smtp';
import type { SendingProfile } from '../../api/smtp';
import { SMTPFormModal } from './SMTPFormModal';
import { SMTPHealthModal } from './SMTPHealthModal';

export function SMTPList() {
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; smtp: SendingProfile | null }>({
    open: false,
    smtp: null,
  });
  const [healthModal, setHealthModal] = useState<{ open: boolean; smtp: SendingProfile | null }>({
    open: false,
    smtp: null,
  });
  const [formModal, setFormModal] = useState<{ open: boolean; smtp: SendingProfile | null }>({
    open: false,
    smtp: null,
  });

  const { data: smtps, isLoading } = useQuery({
    queryKey: ['smtps'],
    queryFn: async () => {
      const res = await smtpApi.getAll();
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => smtpApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smtps'] });
      setDeleteModal({ open: false, smtp: null });
    },
  });

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (smtp: SendingProfile) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <Send className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <p className="font-medium">{smtp.name}</p>
            {smtp.warmup_enabled && (
              <p className="text-xs text-orange-600 flex items-center gap-1">
                <Flame className="w-3 h-3" /> Warmup enabled
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'host',
      header: 'SMTP Server',
      render: (smtp: SendingProfile) => (
        <span className="text-sm text-gray-500">{smtp.host}</span>
      ),
    },
    {
      key: 'from_address',
      header: 'From Address',
      render: (smtp: SendingProfile) => (
        <span className="text-sm text-gray-500">{smtp.from_address}</span>
      ),
    },
    {
      key: 'modified_date',
      header: 'Last Modified',
      render: (smtp: SendingProfile) => (
        <span className="text-sm text-gray-500">
          {new Date(smtp.modified_date).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (smtp: SendingProfile) => (
        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setHealthModal({ open: true, smtp });
            }}
            className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="Delivery health"
          >
            <Activity className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setFormModal({ open: true, smtp });
            }}
            className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteModal({ open: true, smtp });
            }}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="operations-page space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="operations-eyebrow">Delivery infrastructure</p>
          <h1 className="text-2xl font-bold text-gray-900">Sending Profiles</h1>
          <p className="text-gray-500 mt-1">Manage your SMTP configurations</p>
        </div>
        <Button onClick={() => setFormModal({ open: true, smtp: null })}>
          <Plus className="w-4 h-4 mr-2" />
          New Profile
        </Button>
      </div>

      <Card>
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded"></div>
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            data={smtps ?? []}
            keyExtractor={(s) => s.id}
            emptyMessage="No sending profiles yet. Create your first SMTP profile."
          />
        )}
      </Card>

      <SMTPHealthModal
        isOpen={healthModal.open}
        smtp={healthModal.smtp}
        onClose={() => setHealthModal({ open: false, smtp: null })}
      />

      <SMTPFormModal
        isOpen={formModal.open}
        smtp={formModal.smtp}
        onClose={() => setFormModal({ open: false, smtp: null })}
      />

      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, smtp: null })}
        title="Delete Sending Profile"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete "{deleteModal.smtp?.name}"?
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setDeleteModal({ open: false, smtp: null })}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={deleteMutation.isPending}
            onClick={() => deleteModal.smtp && deleteMutation.mutate(deleteModal.smtp.id)}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
